import { useOrganisation } from '@/app/redux/hooks/organisation';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import api from '@/db/apiClient';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Textarea } from '@shadcn/components/textarea';
import { FormEvent, useState } from 'react';
import { toast } from 'sonner';

interface EmailFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EmailFormDialog({
  isOpen,
  onClose,
}: EmailFormDialogProps) {
  const [message, setMessage] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<JSX.Element | string | null>(null);

  const { organisation } = useOrganisation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!organisation) {
      console.error('No organisation found');
      setError(UNKNOWN_ERROR_MESSAGE);
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      await api.asEducator.sendContactFormEmail({
        message,
        organisationName: organisation.name,
      });

      toast.success('Message sent');
      onClose();
      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      setError(
        'Something went wrong. Please try again. If the issue persists, contact rupert@spellcaster.education directly.',
      );
    }
    setIsSending(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Contact us</DialogTitle>
          <DialogDescription>
            Have a question or found something that needs attention? Let us
            know!
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <WarningCircleIcon size={16} className="ml-2" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your message here..."
            className="min-h-[200px] resize-none"
            required
          />

          <div className="flex items-center justify-between pt-4">
            <img
              src="/misc/rupert-icon.png"
              alt="Rupert"
              className="w-8 h-8 rounded-full object-cover mr-2"
            />

            <p className="text-sm text-muted-foreground">
              You can also reach me at{' '}
              <a
                href="mailto:rupert@spellcaster.education"
                className="text-primary hover:underline"
              >
                rupert@spellcaster.education
              </a>
            </p>
            <Button
              type="submit"
              disabled={isSending || !message.trim()}
              loading={isSending}
            >
              Send message
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
